import { LogChangeItem, LogRecipient } from "@/api/LogsService"
import { LogCardVariant } from "@/components/LogCard"
import { AlarmState, Log } from "@/types/logs"

/**
 * Returns a comparator function to sort objects by the order of a specific field's value.
 * @param order Array of field values in desired order
 * @param fieldName The field name to compare
 */
export function sortByFieldOrder<T, K extends keyof T>(
   order: T[K][],
   fieldName: K
) {
   return (a: T, b: T) => {
      return order.indexOf(a[fieldName]) - order.indexOf(b[fieldName])
   }
}

export function getAlarmState(changes?: LogChangeItem[]): AlarmState | null {
   const enabledChange = changes?.find((change) => change.field === "enabled")
   if (!enabledChange) return null

   return enabledChange.newValue ? AlarmState.ENABLED : AlarmState.DISABLED
}

export const formatCardVariant = (logType: string): LogCardVariant => {
   switch (logType) {
      case "Clearance":
         return LogCardVariant.CLEARANCE
      case "Change":
         return LogCardVariant.CHANGE
      case "Creation":
         return LogCardVariant.CREATION
      case "Deletion":
         return LogCardVariant.DELETION
      case "Occupied":
      default:
         return LogCardVariant.TRIGGER
   }
}

function normalizeSensorTypeValue<T>(value: T): T | "both" {
   return Array.isArray(value) && value.length === 2 ? "both" : value
}

export function formatRecipients(recipients: LogRecipient[] | null): string {
   if (!recipients || recipients.length === 0) return "none"

   return recipients
      .map((recipient) => `${recipient.firstName} ${recipient.lastName}`)
      .join(", ")
}

export function formatActive(value: boolean) {
   if (value) {
      return "Yes"
   }
   return "No"
}

export function formatLog(logs: Log[]) {
   return logs
      .map((log) => {
         const logWithLowercaseCardColor = {
            ...log,
            cardColor: log.cardColor?.toLowerCase(),
         }

         if (Array.isArray(log.changes) && log.changes.length > 0) {
            return {
               ...logWithLowercaseCardColor,
               changes: log.changes
                  .slice()
                  .sort(
                     sortByFieldOrder(
                        [
                           "name",
                           "priority",
                           "highLimit",
                           "highTimespan",
                           "cardColor",
                           "typefaceColor",
                           "soundClip",
                           "soundFrequency",
                        ],
                        "field"
                     )
                  )
                  .filter(
                     (change) =>
                        ![
                           "sourceConfiguration",
                           "iconColor",
                           "parents",
                           "type",
                           "address",
                           "levelId",
                           // "sensor",
                           "occupied",
                           "sharedWithExternal",
                           "category",
                           "message",
                           "subject",
                        ].includes(change.field)
                  )
                  .map((change) =>
                     change.field === "sensorTypes"
                        ? {
                             ...change,
                             newValue: normalizeSensorTypeValue(
                                change.newValue
                             ),
                             oldValue: normalizeSensorTypeValue(
                                change.oldValue
                             ),
                          }
                        : change.field === "recipients"
                          ? {
                               ...change,
                               newValue: formatRecipients(
                                  change.newValue as LogRecipient[]
                               ),
                               oldValue: formatRecipients(
                                  change.oldValue as LogRecipient[] | null
                               ),
                            }
                          : change.field === "active"
                            ? {
                                 ...change,
                                 newValue: formatActive(
                                    change.newValue as boolean
                                 ),
                                 oldValue:
                                    change.oldValue === null
                                       ? "none"
                                       : formatActive(
                                            change.oldValue as boolean
                                         ),
                              }
                            : change
                  ),
            }
         }
         return logWithLowercaseCardColor
      })
      .filter(
         (log) =>
            !("levelId" in log) ||
            !!log.changes?.find((change) =>
               ["maintenance", "vacant"].includes(change.field)
            )
      )
}
